import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '@/components/ui';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/Input';
import { YStack } from '@/components/ui/layout/layout';

import { type ResolvableModalFC } from '../../components/modals/modalsManager';

import { subscriptionApis } from './subscription.query';
import { type UpdateAutoRefillRequest } from './subscription.types';
import { UsageAmountInput, isValidUsageAmount } from './usage-amount-input';
import { formatCents } from './usage-format';

interface AutoReloadModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  resolve: (saved: boolean) => void;
  initialThresholdValueCents?: number;
  initialTopupValueCents?: number;
  initialMonthlyCapDollars?: number;
  submitAutoReload?: (
    request: UpdateAutoRefillRequest
  ) => Promise<unknown | null>;
}

const DEFAULT_MONTHLY_CAP_DOLLARS = 1000;
const DEFAULT_THRESHOLD_CENTS = 500;
const DEFAULT_TOPUP_CENTS = 2_000;
const MIN_THRESHOLD_CENTS = 100;
const MAX_THRESHOLD_CENTS = 10_000;
const FALLBACK_MIN_TOPUP_CENTS = 500;
const FALLBACK_MAX_TOPUP_CENTS = 200_000;

export const AutoReloadModal: ResolvableModalFC<
  AutoReloadModalProps,
  boolean
> = ({
  open,
  onOpenChange,
  resolve,
  initialThresholdValueCents,
  initialTopupValueCents,
  initialMonthlyCapDollars = DEFAULT_MONTHLY_CAP_DOLLARS,
  submitAutoReload,
}) => {
  const { data: packsData } = useQuery({
    queryKey: ['credit-packs'],
    queryFn: subscriptionApis.getCreditPacks,
  });
  const minTopupCents = packsData?.min_value_cents ?? FALLBACK_MIN_TOPUP_CENTS;
  const maxTopupCents = packsData?.max_value_cents ?? FALLBACK_MAX_TOPUP_CENTS;

  const [thresholdCents, setThresholdCents] = useState<number | null>(
    initialThresholdValueCents ?? DEFAULT_THRESHOLD_CENTS
  );
  const [topupCents, setTopupCents] = useState<number | null>(
    initialTopupValueCents ?? DEFAULT_TOPUP_CENTS
  );
  const [monthlyCap, setMonthlyCap] = useState(
    String(initialMonthlyCapDollars)
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const sanitize = (raw: string) => raw.replace(/[^\d]/g, '');

  const close = (saved: boolean) => {
    resolve(saved);
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    if (thresholdCents == null || topupCents == null) return;
    if (
      !isValidUsageAmount(
        thresholdCents,
        MIN_THRESHOLD_CENTS,
        MAX_THRESHOLD_CENTS
      )
    ) {
      setError(
        `Threshold must be between ${formatCents(MIN_THRESHOLD_CENTS)} and ${formatCents(MAX_THRESHOLD_CENTS)}.`
      );
      return;
    }
    if (!isValidUsageAmount(topupCents, minTopupCents, maxTopupCents)) {
      setError(
        `Top-up must be between ${formatCents(minTopupCents)} and ${formatCents(maxTopupCents)}.`
      );
      return;
    }
    if (topupCents < thresholdCents) {
      setError('Top-up must be at least your reload threshold.');
      return;
    }
    let monthlyCapCents: number | null = null;
    if (monthlyCap !== '') {
      const monthlyCapDollars = parseInt(monthlyCap, 10);
      if (
        !Number.isFinite(monthlyCapDollars) ||
        monthlyCapDollars < 1 ||
        monthlyCapDollars > 5000
      ) {
        setError('Monthly cap must be between $1 and $5000.');
        return;
      }
      if (monthlyCapDollars * 100 < topupCents) {
        setError('Monthly cap must be at least the top-up amount.');
        return;
      }
      monthlyCapCents = monthlyCapDollars * 100;
    }
    setError(null);
    setSubmitting(true);
    const request: UpdateAutoRefillRequest = {
      enabled: true,
      threshold_value_cents: thresholdCents,
      topup_value_cents: topupCents,
      monthly_cap_cents: monthlyCapCents,
    };
    const result = submitAutoReload
      ? await submitAutoReload(request)
      : await subscriptionApis.updateAutoRefill(request);
    setSubmitting(false);
    if (result) close(true);
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => (o ? onOpenChange(true) : close(false))}
    >
      <Dialog.Content className="max-w-md">
        <Dialog.Header>
          <Dialog.Title>Turn on auto-reload?</Dialog.Title>
          <Dialog.Description>
            Automatically add usage when you&apos;re running low.
          </Dialog.Description>
        </Dialog.Header>

        <YStack className="gap-4 mt-2">
          <YStack className="gap-1.5">
            <label className="text-sm text-foreground">
              When usage drops below:
            </label>
            <UsageAmountInput
              valueCents={thresholdCents}
              onValueCentsChange={setThresholdCents}
              minCents={MIN_THRESHOLD_CENTS}
              maxCents={MAX_THRESHOLD_CENTS}
              disabled={submitting}
              placeholder="5"
            />
          </YStack>

          <YStack className="gap-1.5">
            <label className="text-sm text-foreground">Top up with:</label>
            <UsageAmountInput
              valueCents={topupCents}
              onValueCentsChange={setTopupCents}
              minCents={minTopupCents}
              maxCents={maxTopupCents}
              disabled={submitting}
            />
          </YStack>

          <YStack className="gap-1.5">
            <label className="text-sm text-foreground">
              Don&apos;t spend more than this per month (optional):
            </label>
            <Input
              value={monthlyCap === '' ? '' : `$${monthlyCap}`}
              onChange={(e) => setMonthlyCap(sanitize(e.target.value))}
              placeholder="No limit"
              disabled={submitting}
            />
          </YStack>

          <p className="text-xs text-foreground-muted">
            You agree that Littlebird will charge the card you have on file in
            the amount above on a recurring basis whenever your balance reaches
            the amount indicated. Top-ups are billed at standard usage rates. To
            cancel, turn off auto-reload.
          </p>

          {error && <p className="text-xs text-destructive">{error}</p>}
        </YStack>

        <Dialog.Footer className="mt-4 gap-2">
          <Button
            variant="outline"
            onClick={() => close(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            isLoading={submitting}
            disabled={
              submitting || topupCents == null || thresholdCents == null
            }
          >
            Turn on
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
